/**
 * Which business day a moment falls in (owner concern 2, slice 10.3).
 *
 * `dayStartHour` is the least obvious control in the product and the most
 * consequential: it decides which service an invoice belongs to, and therefore
 * which day's takings it lands in and which Z report it is printed on. The
 * screen that sets it used to render a 600px box containing `5` and a sentence
 * about what the number means — never what it currently DOES.
 *
 * This answers that in one line: at the hour the owner is reading the screen,
 * which date does a sale count as. It mirrors the server's rule (the business
 * day runs from `dayStartHour` to `dayStartHour` the next day, so anything
 * before the boundary belongs to yesterday) without importing anything: the
 * whole calculation is the store's wall clock and one comparison.
 */

/** The store's wall clock at `at`, as calendar parts. */
function wallClock(
  at: Date,
  timezone: string,
): { year: number; month: number; day: number; hour: number } {
  /*
   * `Intl` rather than date arithmetic, because the store's zone is the only
   * clock that matters here and it is the one the browser is least likely to
   * be in: an owner checking the setting from abroad, or a machine left on UTC,
   * must still read the restaurant's own date. An unusable zone string is not
   * an error worth showing — the field above it is where a bad zone is
   * reported — so it falls back to the browser's own clock rather than throwing
   * inside a render.
   */
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
    }).formatToParts(at);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", {
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
    }).formatToParts(at);
  }
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
  };
}

/**
 * The business date `at` belongs to, as `YYYY-MM-DD`.
 *
 * Before the boundary hour the day has not turned over yet, so the sale is
 * yesterday's — which is the entire point of the setting and the thing an owner
 * cannot read off a number between 0 and 23.
 */
export function businessDayOf(
  at: Date,
  dayStartHour: number,
  timezone: string,
): string {
  const clock = wallClock(at, timezone);
  // Built as a UTC date deliberately: these parts are already the store's wall
  // clock, so re-interpreting them in the browser's zone would shift them a
  // second time.
  const date = new Date(
    Date.UTC(clock.year, clock.month - 1, clock.day, 12, 0, 0),
  );
  if (clock.hour < dayStartHour) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return date.toISOString().slice(0, 10);
}
